import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  onSnapshot,
  Timestamp,
  increment
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Analytics Service - Page Views
export const analyticsService = {
  // Track a page view
  async trackPageView(pageName) {
    try {
      const pageViewRef = doc(db, 'pageViews', pageName);
      
      // Try to update first (most common case - document exists)
      // This is more efficient than checking existence first
      try {
        await updateDoc(pageViewRef, {
          count: increment(1),
          lastViewed: Timestamp.now()
        });
        // Success - document existed and was updated
      } catch (updateError) {
        // If update fails because document doesn't exist, create it
        if (updateError.code === 'not-found' || updateError.message?.includes('No document')) {
          // Use setDoc with merge to handle race conditions
          // If another request creates it between updateDoc and setDoc, merge will work
          await setDoc(pageViewRef, {
            pageName: pageName,
            count: 1,
            createdAt: Timestamp.now(),
            lastViewed: Timestamp.now()
          }, { merge: false });
        } else {
          // Other errors - rethrow to be caught by outer catch
          throw updateError;
        }
      }
    } catch (error) {
      // Silently ignore analytics errors - they shouldn't break the app
      // Only log in development mode
      if (process.env.NODE_ENV === 'development') {
        console.debug('Analytics tracking failed (non-critical):', error);
      }
    }
  },

  // Get page view count
  async getPageViewCount(pageName) {
    try {
      const pageViewRef = doc(db, 'pageViews', pageName);
      const pageViewDoc = await getDoc(pageViewRef);
      
      if (pageViewDoc.exists()) {
        return pageViewDoc.data().count || 0;
      }
      return 0;
    } catch (error) {
      console.error('Error getting page view count:', error);
      return 0;
    }
  },

  // Subscribe to page view count updates
  subscribeToPageViewCount(pageName, callback) {
    const pageViewRef = doc(db, 'pageViews', pageName);
    
    return onSnapshot(
      pageViewRef,
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          callback(data.count || 0);
        } else {
          callback(0);
        }
      },
      (error) => {
        console.error('Error subscribing to page view count:', error);
        callback(0);
      }
    );
  },

  // Track a session-level public view
  async trackSessionView(sessionId, courseId = null, viewerId = null) {
    if (!sessionId) return;

    try {
      const sessionViewRef = doc(db, 'sessionViewStats', sessionId);

      try {
        await updateDoc(sessionViewRef, {
          courseId: courseId || null,
          viewCount: increment(1),
          lastViewed: Timestamp.now()
        });
      } catch (updateError) {
        if (updateError.code === 'not-found' || updateError.message?.includes('No document')) {
          await setDoc(
            sessionViewRef,
            {
              sessionId,
              courseId: courseId || null,
              viewCount: 1,
              uniqueViewers: viewerId ? 1 : 0,
              createdAt: Timestamp.now(),
              lastViewed: Timestamp.now()
            },
            { merge: false }
          );
        } else {
          throw updateError;
        }
      }

      if (viewerId) {
        const sessionViewerRef = doc(db, 'sessionViewStats', sessionId, 'viewers', viewerId);
        try {
          await updateDoc(sessionViewerRef, {
            lastViewed: Timestamp.now(),
            viewCount: increment(1)
          });
        } catch (viewerError) {
          if (viewerError.code === 'not-found' || viewerError.message?.includes('No document')) {
            await setDoc(sessionViewerRef, {
              viewerId,
              firstViewed: Timestamp.now(),
              lastViewed: Timestamp.now(),
              viewCount: 1
            });

            await updateDoc(sessionViewRef, {
              uniqueViewers: increment(1)
            });
          } else {
            throw viewerError;
          }
        }
      }

      if (courseId) {
        await this.trackCourseView(courseId, viewerId);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Session view tracking failed (non-critical):', error);
      }
    }
  },

  // Track a course-level public view
  async trackCourseView(courseId, viewerId = null) {
    if (!courseId) return;

    try {
      const courseViewRef = doc(db, 'courseViewStats', courseId);

      try {
        await updateDoc(courseViewRef, {
          viewCount: increment(1),
          lastViewed: Timestamp.now()
        });
      } catch (updateError) {
        if (updateError.code === 'not-found' || updateError.message?.includes('No document')) {
          await setDoc(
            courseViewRef,
            {
              courseId,
              viewCount: 1,
              uniqueViewers: viewerId ? 1 : 0,
              createdAt: Timestamp.now(),
              lastViewed: Timestamp.now()
            },
            { merge: false }
          );
        } else {
          throw updateError;
        }
      }

      if (viewerId) {
        const courseViewerRef = doc(db, 'courseViewStats', courseId, 'viewers', viewerId);
        try {
          await updateDoc(courseViewerRef, {
            lastViewed: Timestamp.now(),
            viewCount: increment(1)
          });
        } catch (viewerError) {
          if (viewerError.code === 'not-found' || viewerError.message?.includes('No document')) {
            await setDoc(courseViewerRef, {
              viewerId,
              firstViewed: Timestamp.now(),
              lastViewed: Timestamp.now(),
              viewCount: 1
            });

            await updateDoc(courseViewRef, {
              uniqueViewers: increment(1)
            });
          } else {
            throw viewerError;
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Course view tracking failed (non-critical):', error);
      }
    }
  },

  // Fetch session view stats
  async getSessionViewStats(sessionId) {
    if (!sessionId) {
      return { viewCount: 0, uniqueViewers: 0 };
    }

    try {
      const sessionViewRef = doc(db, 'sessionViewStats', sessionId);
      const snapshot = await getDoc(sessionViewRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        return {
          viewCount: data.viewCount || 0,
          uniqueViewers: data.uniqueViewers || 0,
          lastViewed: data.lastViewed || null,
          courseId: data.courseId || null
        };
      }
      return { viewCount: 0, uniqueViewers: 0 };
    } catch (error) {
      if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
        console.log('Session view stats not available (permission denied) - using defaults');
        return { viewCount: 0, uniqueViewers: 0 };
      }
      console.error('Error getting session view stats:', error);
      return { viewCount: 0, uniqueViewers: 0 };
    }
  },

  // Fetch course view stats
  async getCourseViewStats(courseId) {
    if (!courseId) {
      return { viewCount: 0, uniqueViewers: 0 };
    }

    try {
      const courseViewRef = doc(db, 'courseViewStats', courseId);
      const snapshot = await getDoc(courseViewRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        return {
          viewCount: data.viewCount || 0,
          uniqueViewers: data.uniqueViewers || 0,
          lastViewed: data.lastViewed || null
        };
      }
      return { viewCount: 0, uniqueViewers: 0 };
    } catch (error) {
      if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
        console.log('Course view stats not available (permission denied) - using defaults');
        return { viewCount: 0, uniqueViewers: 0 };
      }
      console.error('Error getting course view stats:', error);
      return { viewCount: 0, uniqueViewers: 0 };
    }
  },

  // Seed or overwrite session view stats (admin backfill)
  async setSessionViewStats(sessionId, stats = {}) {
    if (!sessionId) return;

    try {
      const { viewCount = 0, uniqueViewers = 0, courseId = null } = stats;
      const sessionViewRef = doc(db, 'sessionViewStats', sessionId);
      await setDoc(
        sessionViewRef,
        {
          sessionId,
          courseId,
          viewCount,
          uniqueViewers,
          lastViewed: Timestamp.now(),
          createdAt: Timestamp.now()
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error seeding session view stats:', error);
    }
  },

  // Seed or overwrite course view stats (admin backfill)
  async setCourseViewStats(courseId, stats = {}) {
    if (!courseId) return;

    try {
      const { viewCount = 0, uniqueViewers = 0 } = stats;
      const courseViewRef = doc(db, 'courseViewStats', courseId);
      await setDoc(
        courseViewRef,
        {
          courseId,
          viewCount,
          uniqueViewers,
          lastViewed: Timestamp.now(),
          createdAt: Timestamp.now()
        },
        { merge: true }
      );
    } catch (error) {
      console.error('Error seeding course view stats:', error);
    }
  },

  // Track new registration
  async trackRegistration(courseId, sessionId) {
    try {
      const registrationRef = doc(db, 'registrationStats', `${courseId}_${sessionId}`);
      
      try {
        await updateDoc(registrationRef, {
          attempted: increment(1),
          lastUpdated: Timestamp.now()
        });
      } catch (updateError) {
        if (updateError.code === 'not-found' || updateError.message?.includes('No document')) {
          await setDoc(registrationRef, {
            courseId: courseId,
            sessionId: sessionId,
            attempted: 1,
            completed: 0,
            createdAt: Timestamp.now(),
            lastUpdated: Timestamp.now()
          }, { merge: false });
        } else {
          throw updateError;
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Registration tracking failed (non-critical):', error);
      }
    }
  },

  // Track completed registration
  async trackCompletedRegistration(courseId, sessionId) {
    try {
      const registrationRef = doc(db, 'registrationStats', `${courseId}_${sessionId}`);
      
      try {
        await updateDoc(registrationRef, {
          completed: increment(1),
          lastUpdated: Timestamp.now()
        });
      } catch (updateError) {
        if (updateError.code === 'not-found' || updateError.message?.includes('No document')) {
          await setDoc(registrationRef, {
            courseId: courseId,
            sessionId: sessionId,
            attempted: 0,
            completed: 1,
            createdAt: Timestamp.now(),
            lastUpdated: Timestamp.now()
          }, { merge: true });
        } else {
          throw updateError;
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('Completed registration tracking failed (non-critical):', error);
      }
    }
  },

  // Get registration stats
  async getRegistrationStats(courseId, sessionId) {
    try {
      const registrationRef = doc(db, 'registrationStats', `${courseId}_${sessionId}`);
      const registrationDoc = await getDoc(registrationRef);
      
      if (registrationDoc.exists()) {
        return registrationDoc.data();
      }
      return {
        attempted: 0,
        completed: 0,
        createdAt: Timestamp.now(),
        lastUpdated: Timestamp.now()
      };
    } catch (error) {
      // Silently handle permission errors - these are non-critical
      if (error.code === 'permission-denied' || error.message?.includes('Missing or insufficient permissions')) {
        console.log('Registration stats not available (permission denied) - using defaults');
        return {
          attempted: 0,
          completed: 0,
          createdAt: Timestamp.now(),
          lastUpdated: Timestamp.now()
        };
      }
      console.error('Error getting registration stats:', error);
      return {
        attempted: 0,
        completed: 0,
        createdAt: Timestamp.now(),
        lastUpdated: Timestamp.now()
      };
    }
  },

  // Subscribe to registration stats updates
  subscribeToRegistrationStats(courseId, sessionId, callback) {
    const registrationRef = doc(db, 'registrationStats', `${courseId}_${sessionId}`);
    
    return onSnapshot(
      registrationRef,
      (snapshot) => {
        if (snapshot.exists()) {
          callback(snapshot.data());
        } else {
          callback({
            attempted: 0,
            completed: 0,
            createdAt: Timestamp.now(),
            lastUpdated: Timestamp.now()
          });
        }
      },
      (error) => {
        console.error('Error subscribing to registration stats:', error);
        callback({
          attempted: 0,
          completed: 0,
          createdAt: Timestamp.now(),
          lastUpdated: Timestamp.now()
        });
      }
    );
  }
};

